import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, ShieldAlert, Copy, Check, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PHASES } from "@/lib/beta-checklist";

/**
 * Beta reports (admin). Every report testers sent from the Beta test page,
 * newest first; open one for its results, comments and the log it carried.
 *
 * Items are shown in checklist order with the title the TESTER saw (stored in
 * the report), so an old report still reads correctly after the checklist is
 * reworded.
 */

const listReports = (status) => api.get("/beta/reports", { params: { status } }).then((r) => r.data);
const getReport = (id) => api.get(`/beta/reports/${id}`).then((r) => r.data);
const reviewReport = (id, body) => api.patch(`/beta/reports/${id}`, body).then((r) => r.data);
const archiveReviewed = () => api.post("/beta/reports/archive-reviewed").then((r) => r.data);

// New when sent, Reviewed once read, Archived when dealt with (migration 014).
// Nothing is deleted: each state is a tab.
const TABS = [
  { key: "new", label: "New" },
  { key: "reviewed", label: "Reviewed" },
  { key: "archived", label: "Archived" },
];
const CHIP = {
  new: "bg-primary/15 text-primary",
  reviewed: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  archived: "bg-secondary text-muted-foreground",
};

const ORDER = PHASES.flatMap((p) => p.items.map((i) => [i.id, p.name]));
const PHASE_OF = Object.fromEntries(ORDER);
const RANK = Object.fromEntries(ORDER.map(([id], i) => [id, i]));

const when = (s) => new Date(Number(s) * 1000).toISOString().replace("T", " ").slice(0, 16) + "Z";
const who = (r) => `${r.pid} ${[r.first_name, r.last_name].filter(Boolean).join(" ")}`.trim();
const tone = { pass: "text-emerald-600 dark:text-emerald-400", fail: "text-primary", skip: "text-amber-600 dark:text-amber-400" };

export default function BetaReports() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("new");
  const [openId, setOpenId] = useState(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["beta-reports", tab], queryFn: () => listReports(tab),
    enabled: !!user?.is_admin, refetchInterval: 60_000,
  });
  const reports = data?.items || [];
  const counts = data?.counts || {};
  useEffect(() => { setOpenId(null); }, [tab]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["beta-reports"] });
  const bulk = useMutation({
    mutationFn: archiveReviewed,
    onSuccess: (r) => { toast.success(`${r.archived} report${r.archived === 1 ? "" : "s"} archived`); setOpenId(null); refresh(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not archive"),
  });
  // After a report leaves this tab, open the next one down (or the one above).
  const advance = (id) => {
    const i = reports.findIndex((r) => r.id === id);
    const next = reports[i + 1] || reports[i - 1];
    setOpenId(next && next.id !== id ? next.id : null);
  };

  if (!user?.is_admin) {
    return (
      <div className="p-8"><div className="card-flat p-10 text-center text-sm text-muted-foreground">
        <ShieldAlert className="w-6 h-6 mx-auto mb-2" />Beta reports are for admins.
      </div></div>
    );
  }

  return (
    <div className="p-8 max-w-[1400px]" data-testid="beta-reports-page">
      <div className="mb-6">
        <div className="overline mb-1">Admin</div>
        <h1 className="text-3xl font-bold tracking-tight">Beta reports</h1>
        <div className="acbar" />
        <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
          Test flights sent from the Beta test page. New reports are also posted to the beta Discord
          channel when one is configured.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4" role="tablist" data-testid="beta-report-tabs">
        {TABS.map((t) => (
          <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
            data-testid={`beta-tab-${t.key}`}
            className={`text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border ${
              tab === t.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
            {t.label} <span className="font-normal opacity-80">{counts[t.key] ?? 0}</span>
          </button>
        ))}
        {tab === "reviewed" && reports.length > 0 && (
          <button type="button" disabled={bulk.isPending}
            onClick={() => window.confirm(`Archive all ${counts.reviewed} reviewed reports? They stay readable under Archived.`) && bulk.mutate()}
            className="ml-auto text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border border-border hover:bg-secondary flex items-center gap-2"
            data-testid="beta-archive-reviewed">
            <Archive className="w-3.5 h-3.5" /> Archive all reviewed
          </button>
        )}
        {data && data.reviewing === false && (
          <span className="text-xs text-amber-600 dark:text-amber-400 ml-auto">
            Reviewing needs migration 014 on the server; until then every report shows as New.
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : error ? (
        <div className="card-flat p-6 text-sm text-primary">
          {error.response?.status === 500 || error.response?.status === 503
            ? "Reports could not be loaded. If this is a new server, migration 010_beta_reports.sql may not have been applied yet."
            : error.response?.data?.detail || "Reports could not be loaded."}
        </div>
      ) : reports.length === 0 ? (
        <div className="card-flat p-10 text-center text-sm text-muted-foreground">
          {tab === "new" ? "Nothing new. All caught up." : tab === "reviewed" ? "No reviewed reports waiting to be archived." : "Nothing archived yet."}
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
          <div className="card-flat divide-y divide-border self-start" data-testid="beta-report-list">
            {reports.map((r) => (
              <button key={r.id} type="button" onClick={() => setOpenId(r.id)}
                className={`w-full text-left px-4 py-3 hover:bg-secondary transition-colors ${openId === r.id ? "bg-secondary" : ""}`}>
                <div className="flex flex-wrap items-baseline gap-x-3 text-sm">
                  <span className="font-mono text-xs text-muted-foreground">#{r.id}</span>
                  {r.admin_note && <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1">note</span>}
                  <span className="font-semibold">{who(r)}</span>
                  <span className="text-muted-foreground">{r.setup_label}</span>
                  <span className="ml-auto font-mono text-xs text-muted-foreground">{when(r.created_at)}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 text-xs font-mono tabular-nums mt-1">
                  <span className={tone.pass}>{r.n_pass} pass</span>
                  <span className={tone.fail}>{r.n_fail} fail</span>
                  <span className={tone.skip}>{r.n_skip} skip</span>
                  {r.n_open > 0 && <span className="text-muted-foreground">{r.n_open} not done</span>}
                  <span className="text-muted-foreground ml-auto">{r.app_version || "?"}{r.flight ? ` · ${r.flight}` : ""}</span>
                </div>
              </button>
            ))}
          </div>
          {openId ? <ReportDetail id={openId} onMoved={() => { advance(openId); refresh(); }} /> : (
            <div className="card-flat p-10 text-sm text-muted-foreground text-center self-start">Choose a report to read it.</div>
          )}
        </div>
      )}
    </div>
  );
}

function ReportDetail({ id, onMoved }) {
  const qc = useQueryClient();
  const { data: r, isLoading } = useQuery({ queryKey: ["beta-report", id], queryFn: () => getReport(id) });
  const [note, setNote] = useState("");
  useEffect(() => { setNote(r?.admin_note || ""); }, [r?.id, r?.admin_note]);

  const review = useMutation({
    mutationFn: (body) => reviewReport(id, body),
    onSuccess: (row, body) => {
      qc.setQueryData(["beta-report", id], (old) => (old ? { ...old, ...row } : old));
      if ("status" in body) {
        toast.success(body.status === "reviewed" ? "Marked reviewed" : body.status === "archived" ? "Archived" : "Back in New");
        onMoved();
      } else {
        toast.success("Note saved");
        qc.invalidateQueries({ queryKey: ["beta-reports"] });
      }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not update the report"),
  });

  if (isLoading || !r) return <div className="card-flat p-6 text-sm text-muted-foreground self-start">Loading report…</div>;

  const items = Object.entries(r.results || {}).sort(([a], [b]) => (RANK[a] ?? 999) - (RANK[b] ?? 999));
  const fails = items.filter(([, v]) => v.status === "fail");
  const copyLog = async () => {
    try { await navigator.clipboard.writeText(r.log_excerpt || ""); toast.success("Log copied"); }
    catch { toast.error("Could not copy; select the log text instead"); }
  };

  return (
    <article className="card-flat p-5 space-y-5 self-start min-w-0" data-testid="beta-report-detail">
      <div className="flex flex-wrap items-center gap-2 pb-4 border-b border-border" data-testid="beta-report-actions">
        <span className={`text-[11px] font-bold uppercase tracking-widest px-2 py-1 rounded ${CHIP[r.status] || CHIP.new}`}>
          {r.status || "new"}
        </span>
        {r.reviewed_by && (
          <span className="text-xs text-muted-foreground">by {r.reviewed_by}{r.reviewed_at ? ` · ${when(r.reviewed_at)}` : ""}</span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          {r.status !== "reviewed" && r.status !== "archived" && (
            <button type="button" disabled={review.isPending} onClick={() => review.mutate({ status: "reviewed" })}
              className="text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md bg-primary text-primary-foreground hover:brightness-110 flex items-center gap-1.5"
              data-testid="beta-mark-reviewed">
              <Check className="w-3.5 h-3.5" /> Mark reviewed
            </button>
          )}
          {r.status !== "archived" && (
            <button type="button" disabled={review.isPending} onClick={() => review.mutate({ status: "archived" })}
              className="text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border border-border hover:bg-secondary flex items-center gap-1.5"
              data-testid="beta-archive">
              <Archive className="w-3.5 h-3.5" /> Archive
            </button>
          )}
          {r.status && r.status !== "new" && (
            <button type="button" disabled={review.isPending} onClick={() => review.mutate({ status: "new" })}
              className="text-xs font-bold uppercase tracking-widest px-3 py-2 rounded-md border border-border hover:bg-secondary flex items-center gap-1.5"
              data-testid="beta-reopen">
              <RotateCcw className="w-3.5 h-3.5" /> Reopen
            </button>
          )}
        </div>
      </div>

      <section>
        <h2 className="overline mb-2">Admin note</h2>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000}
          placeholder="For the other admins: what was done, which version fixes it…"
          className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm" data-testid="beta-admin-note" />
        {note !== (r.admin_note || "") && (
          <button type="button" disabled={review.isPending} onClick={() => review.mutate({ note })}
            className="mt-2 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-md border border-border hover:bg-secondary">
            Save note
          </button>
        )}
      </section>

      <header>
        <div className="text-lg font-bold">#{r.id} · {who(r)}</div>
        <div className="text-sm text-muted-foreground">
          {r.setup_label} · {r.app_version || "version ?"} · checklist v{r.checklist_version} · {when(r.created_at)}
          {r.flight ? ` · ${r.flight}` : ""}{r.aircraft ? ` · ${r.aircraft}` : ""}{r.live_id ? ` · flight ${r.live_id}` : ""}
        </div>
      </header>

      {fails.length > 0 && (
        <section>
          <h2 className="overline mb-2">Failures</h2>
          <ul className="space-y-2">
            {fails.map(([k, v]) => (
              <li key={k} className="border-l-4 border-primary pl-3 text-sm">
                <div className="font-semibold">{v.t || k}</div>
                {v.note && <div className="whitespace-pre-wrap text-muted-foreground">{v.note}</div>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {r.comments && (
        <section>
          <h2 className="overline mb-2">Comments</h2>
          <p className="text-sm whitespace-pre-wrap">{r.comments}</p>
        </section>
      )}

      <section>
        <h2 className="overline mb-2">All results</h2>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-border">
            {items.map(([k, v]) => (
              <tr key={k} className="align-top">
                <td className="py-1.5 pr-3 text-xs text-muted-foreground whitespace-nowrap">{PHASE_OF[k] || "—"}</td>
                <td className="py-1.5 pr-3">{v.t || k}{v.note && v.status !== "fail" ? <div className="text-xs text-muted-foreground whitespace-pre-wrap">{v.note}</div> : null}</td>
                <td className={`py-1.5 font-mono text-xs uppercase ${tone[v.status] || ""}`}>{v.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section>
        <div className="flex items-center mb-2">
          <h2 className="overline">Log</h2>
          {r.log_excerpt && (
            <button type="button" onClick={copyLog} className="ml-auto text-xs text-primary hover:underline flex items-center gap-1">
              <Copy className="w-3 h-3" /> Copy log
            </button>
          )}
        </div>
        {r.log_excerpt ? (
          <pre className="text-[11px] leading-relaxed font-mono bg-secondary border border-border rounded-md p-3 max-h-96 overflow-auto whitespace-pre">{r.log_excerpt}</pre>
        ) : (
          <p className="text-sm text-muted-foreground">No log attached (sent from a browser, or the tester left it out).</p>
        )}
      </section>
    </article>
  );
}
